// Typed API client for fabric CRUD + asset uploads.
// Wraps the centralized axios instance (session-cookie auth).
import api from "./api";
import { IFabric } from "@/models/fabric";

/**
 * A staged clockwise rotation, in quarter-turns. Mirrors RotateDeg in the API's
 * fabric_asset_service. 0 means "don't rotate" and is never sent.
 *
 * Only right angles: they are a lossless pixel remap and they preserve
 * tileability, so a seamless texture stays seamless.
 */
export type RotateDeg = 0 | 90 | 180 | 270;

export interface SwatchUploadResult {
  image: string;
  texture_key: string;
  swatchUrl: string;
  textureUrl: string;
  cacheBust: string | number;
}

export interface TextureUploadResult {
  texture_key: string;
  textureUrl: string;
  cacheBust: string | number;
}

export interface FabricAssetStatus {
  texture_key: string;
  swatch: boolean;
  texture: boolean;
}

export interface RotateResult {
  texture_key: string;
  asset: "swatch" | "texture";
  degrees: number;
  url: string;
  cacheBust: string | number;
}

export default class FabricService {
  /**
   * In-memory cache of the FULL catalog (the un-graded `list()`), shared at class
   * level. Three separate consumers want the whole pool — the home grid, the
   * editor's brand/mfr dropdowns, and the header's jump-to-fabric search — and
   * without this, every navigation would refetch ~2k fabrics to answer a question
   * the last request already answered.
   *
   * Invalidated by every write below, so a fabric renamed in the editor is
   * findable under its new name from the header the moment the save lands.
   */
  private static catalog: IFabric[] | null = null;
  private static inflight: Promise<IFabric[]> | null = null;

  public static invalidate(): void {
    FabricService.catalog = null;
    FabricService.inflight = null;
  }

  // GET /fabrics?include=all  (optionally &grade=)
  //
  // `include=all` — not `admin=true` — because this is the tool that has to be
  // able to SEE a `hidden` fabric in order to un-hide it. `admin=true` is the
  // Shade Designer's admin browse: everything except the hidden set. Both now
  // require a real Admin session server-side (they used to be honored on the
  // query param alone, which meant any apikey holder could request them).
  //
  // Only the un-graded call is cached — it is the one every view asks for, and a
  // grade-scoped result is a subset that would answer the wrong question if it
  // were ever served back as "the catalog".
  public async list(grade?: string, force = false): Promise<IFabric[]> {
    const params: Record<string, string> = { include: "all" };
    if (grade) {
      params.grade = grade;
      const res = await api.get("/fabrics", { params });
      return (res.data?.data ?? []) as IFabric[];
    }

    if (!force && FabricService.catalog) return FabricService.catalog;
    // De-duplicate concurrent callers: the editor loads the fabric and its
    // dropdown options at once, and we don't need two requests for one answer.
    if (!force && FabricService.inflight) return FabricService.inflight;

    const req = api
      .get("/fabrics", { params })
      .then((res) => {
        const data = (res.data?.data ?? []) as IFabric[];
        FabricService.catalog = data;
        FabricService.inflight = null;
        return data;
      })
      .catch((err) => {
        // Don't leave a rejected promise parked in `inflight` — every later
        // caller would inherit the same failure and the app could never recover.
        FabricService.inflight = null;
        throw err;
      });
    FabricService.inflight = req;
    return req;
  }

  // GET /fabrics/:mfr_code — returns an array (mfr_code is not unique on its
  // own; duplicates are disambiguated by name.group). We pick the first match,
  // optionally narrowed by group.
  public async getByMfrCode(mfrCode: string, group?: string): Promise<IFabric | null> {
    const params: Record<string, string> = {};
    if (group) params.group = group;
    const res = await api.get(`/fabrics/${encodeURIComponent(mfrCode)}`, { params });
    const data = (res.data?.data ?? []) as IFabric[];
    return data.length ? data[0] : null;
  }

  // POST /fabrics
  public async create(fabric: IFabric): Promise<IFabric> {
    const res = await api.post("/fabrics", fabric);
    FabricService.invalidate();
    return (res.data?.data ?? res.data) as IFabric;
  }

  // PUT /fabrics/update/:id — send the full doc so untouched fields
  // (comparisons, specs, etc.) are never clobbered.
  public async update(fabric: IFabric): Promise<IFabric> {
    const res = await api.put(`/fabrics/update/${fabric._id}`, fabric);
    FabricService.invalidate();
    return (res.data?.data ?? res.data) as IFabric;
  }

  // DELETE /fabrics/delete/:id
  public async remove(id: string): Promise<void> {
    await api.delete(`/fabrics/delete/${id}`);
    FabricService.invalidate();
  }

  // POST /fabrics/:id/swatch  (multipart/form-data)
  // Replaces the swatch; by default also seeds the texture.
  //
  // `degrees` is a staged clockwise rotation to bake in. The API folds it into the
  // re-encode it already runs on every upload, so shipping it with the file costs
  // nothing — whereas uploading and then calling rotateAsset would spend a second
  // lossy JPEG generation on the same image.
  public async uploadSwatch(
    id: string,
    file: File,
    copyToTextures = true,
    degrees: RotateDeg = 0,
    onProgress?: (pct: number) => void
  ): Promise<SwatchUploadResult> {
    const form = new FormData();
    form.append("file", file);
    form.append("copyToTextures", String(copyToTextures));
    if (degrees) form.append("degrees", String(degrees));
    const res = await api.post(`/fabrics/${id}/swatch`, form, {
      // Don't set Content-Type — the browser must add multipart/form-data WITH
      // the boundary, or express-fileupload can't parse req.files server-side.
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
    // The stored `image` and the asset cache-bust both just moved; the cached
    // catalog is carrying the old ones, and the grid renders its thumbnails from
    // exactly those two fields.
    FabricService.invalidate();
    return res.data.data as SwatchUploadResult;
  }

  // POST /fabrics/:id/texture  (multipart/form-data)
  // Replaces ONLY the seamless texture; leaves the swatch untouched.
  public async uploadTexture(
    id: string,
    file: File,
    degrees: RotateDeg = 0,
    onProgress?: (pct: number) => void
  ): Promise<TextureUploadResult> {
    const form = new FormData();
    form.append("file", file);
    if (degrees) form.append("degrees", String(degrees));
    const res = await api.post(`/fabrics/${id}/texture`, form, {
      // Don't set Content-Type — the browser must add multipart/form-data WITH
      // the boundary, or express-fileupload can't parse req.files server-side.
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
    FabricService.invalidate();
    return res.data.data as TextureUploadResult;
  }

  // GET /fabrics/:id/assets — which asset files actually exist on Wasabi.
  // A fabric can hold a texture_key without a texture file ever having been
  // written, so the DB can't answer this; the API HEAD-probes the bucket.
  public async assetStatus(id: string): Promise<FabricAssetStatus> {
    const res = await api.get(`/fabrics/${id}/assets`);
    return res.data.data as FabricAssetStatus;
  }

  // POST /fabrics/:id/texture/from-swatch
  // Seeds the 3D texture from the fabric's existing swatch. Server-side copy —
  // no bytes are uploaded from the browser.
  //
  // A non-zero `degrees` forces the API onto a slower path (a byte copy cannot
  // re-orient anything, so it must re-encode the swatch instead). That is the
  // inherent cost of wanting the swatch turned, not an inefficiency to avoid.
  public async copyTextureFromSwatch(
    id: string,
    degrees: RotateDeg = 0
  ): Promise<TextureUploadResult> {
    const res = await api.post(`/fabrics/${id}/texture/from-swatch`, { degrees });
    FabricService.invalidate();
    return res.data.data as TextureUploadResult;
  }

  // POST /fabrics/:id/rotate
  // Rotates the stored asset 90° clockwise in place on Wasabi — for directional
  // fabrics (e.g. a stripe that needs to run horizontally rather than vertically).
  // The bytes are re-encoded server-side; nothing is uploaded from the browser.
  public async rotateAsset(
    id: string,
    asset: "swatch" | "texture",
    degrees: 90 | 180 | 270 = 90
  ): Promise<RotateResult> {
    const res = await api.post(`/fabrics/${id}/rotate`, { asset, degrees });
    FabricService.invalidate();
    return res.data.data as RotateResult;
  }
}
